import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { I18nProvider } from "@/contexts/I18nProvider";
import { LocationProvider } from "@/contexts/LocationContext";
import { BGMProvider } from "@/contexts/BGMContext";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { VersionCheckDialog } from "@/components/VersionCheckDialog";
import { InstallCTA } from "@/components/pwa/InstallCTA";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DebugOverlay } from "@/components/DebugOverlay";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";
import { ThemeProvider } from "next-themes";
import { AnalyticsProvider } from "@/contexts/AnalyticsContext";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/safeClient";
import { useNotifications } from "@/hooks/useNotifications";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { useBackgroundDealTracker } from "@/hooks/useBackgroundDealTracker";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,      // 5 minutes
      gcTime: 1000 * 60 * 30,         // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const { user } = useAuth();
  
  useNotifications();
  usePushSubscription();
  useRoutePrefetch();
  useBackgroundDealTracker();

  useEffect(() => {
    return () => {
      supabase.removeAllChannels();
    };
  }, []);

  return (
    <>
      <main id="main-content">
        <AnimatedRoutes />
      </main>
      {import.meta.env.PROD && <InstallCTA />}
    </>
  );
}

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              {import.meta.env.PROD && <PWAUpdatePrompt />}
              <VersionCheckDialog />
              <BrowserRouter future={{ v7_relativeSplatPath: true }}>
                <AuthProvider>
                  <RealtimeProvider>
                    <LocationProvider>
                      <BGMProvider>
                        <AnalyticsProvider>
                          <AppContent />
                        </AnalyticsProvider>
                        <DebugOverlay />
                      </BGMProvider>
                    </LocationProvider>
                  </RealtimeProvider>
                </AuthProvider>
              </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
